import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../store";
import type { PayloadAction } from "@reduxjs/toolkit";
import { trackEvent } from "../../utils/analytics";

// Define the lead state types
interface LeadState {
  leadParams: {
    email: string | null;
    phone: string | null;
    message: string | null;
  };
  inputErrors: {
    email: string | null;
    phone: string | null;
    message: string | null;
  };
  loading: boolean;
  canSubmit: boolean;
  feedbackMessage: {
    message: string | null;
    isError: boolean;
    open: boolean;
  };
}

type initialStateType = {
  leadState: LeadState;
};

const initialState: initialStateType = {
  leadState: {
    leadParams: {
      email: null,
      phone: null,
      message: null,
    },
    inputErrors: {
      email: null,
      phone: null,
      message: null,
    },
    loading: false,
    canSubmit: false,
    feedbackMessage: {
      message: null,
      isError: false,
      open: false,
    },
  },
};

// Validation function for leads
export const validateContactForm = (values: any) => {
  let errors = {} as any;

  // Email validation
  if (!values.email) {
    errors.email = "Email is required";
  } else if (!/\S+@\S+\.\S+/.test(values.email)) {
    errors.email = "Email is invalid";
  }

  // Phone validation
  if (!values.phone) {
    errors.phone = "Phone number is required";
  } else if (!/^(?:\+?254|0)?7|1\d{8}$/.test(values.phone)) {
    errors.phone = "Phone number is invalid";
  }

  // Message validation
  if (!values.message) {
    errors.message = "Message is required";
  } else if (values.message.length < 10) {
    errors.message = "Message must be at least 10 characters";
  }

  return errors;
};

export const leadsSlice = createSlice({
  name: "leads",
  initialState,
  reducers: {
    setLeadParams: (state, action: PayloadAction<any>) => {
      if (action.payload.values) {
        state.leadState.leadParams = {
          ...state.leadState.leadParams,
          ...action.payload.values
        };
      }
      const errors = validateContactForm(state.leadState.leadParams);
      state.leadState.inputErrors = errors;
      state.leadState.canSubmit = Object.keys(errors).length === 0;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.leadState.loading = action.payload;
    },
    saveLead: (state, action) => {
      if (action.payload.methodRequestState === 'requested') {
        state.leadState.loading = true;
      } else if (action.payload.methodRequestState === 'finished') {
        trackEvent('Leads saved', {
          email: state.leadState.leadParams.email,
          phone: state.leadState.leadParams.phone,
          message: state.leadState.leadParams.message,
        });
        if (action.payload.result) {
          state.leadState.feedbackMessage = {
            message: 'Your message has been received. We will act accordingly.',
            open: true,
            isError: false,
          };
          state.leadState.leadParams = initialState.leadState.leadParams;
        } else {
          state.leadState.feedbackMessage = {
            message: action.payload?.error?.message || 'Failed to send your message. Please try again.',
            isError: true,
            open: true,
          };
        }
        state.leadState.loading = false;
      }
    },
    resetFeedbackMessage: (state) => {
      state.leadState.feedbackMessage = {
        message: null,
        open: false,
        isError: false,
      };
    },
  },
});

export const {
  setLeadParams,
  setLoading,
  saveLead,
  resetFeedbackMessage,
} = leadsSlice.actions;

export const saveLeadAction = ({ params, method }: { params: any; method: { url: string; action: string } }) => ({
  type: saveLead.type,
  payload: {
    params,
    method,
  }
});

export default leadsSlice.reducer;

export const selectLeadState = (state: RootState) => state.leads.leadState; 